import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CabanaWithDetails } from 'store/api-slice/types'
import { v4 as uuidv4 } from 'uuid'

export type CabanaType = 'cabanas'

export type CabanaItemType = {
    cabana: CabanaWithDetails
    isCompleted: boolean
    isSubmitted: boolean
}

interface CabanaState {
    cabanas: CabanaItemType[]
    defaultDate: string
    defaultUnitPrice: number
    defaultProductId: string
    edited: {
        index: number
        origin: CabanaItemType
    } | null
}

export const DEFAULT_PRODUCT_TYPE = 'cabana'
export const DEFAULT_QUANTITY = 1

export const emptyCabana: CabanaWithDetails = {
    cartItemId: '',
    productId: '',
    productType: DEFAULT_PRODUCT_TYPE,
    quantity: DEFAULT_QUANTITY,
    detail: {
        date: '',
        boxId: '',
    },
}

const initialState: CabanaState = {
    cabanas: [],
    defaultDate: '',
    defaultUnitPrice: 0,
    defaultProductId: '',
    edited: null,
}

export const checkIfCabanaCompleted = (date: string) => date !== ''

const cabanaSlice = createSlice({
    name: 'cabana',
    initialState,
    reducers: {
        setInitialData(
            state,
            action: PayloadAction<{
                date?: string
                unitPrice?: number
                productId?: string
            }>
        ) {
            const { date, unitPrice, productId } = action.payload
            const { defaultDate, defaultUnitPrice, defaultProductId } = state

            state.defaultDate = date !== undefined ? date : defaultDate
            state.defaultUnitPrice =
                unitPrice !== undefined ? unitPrice : defaultUnitPrice
            state.defaultProductId =
                productId !== undefined ? productId : defaultProductId
        },

        addCabana(
            state,
            action: PayloadAction<{
                cabana?: CabanaItemType
            }>
        ) {
            const { cabana } = action.payload
            const { defaultDate, defaultProductId } = state

            if (cabana) {
                state.cabanas = [...state.cabanas, cabana]
                return
            }

            const newCabana: CabanaItemType = {
                cabana: {
                    ...emptyCabana,
                    cartItemId: uuidv4(),
                    productId: defaultProductId,
                    detail: {
                        date: defaultDate,
                        boxId: uuidv4(),
                    },
                },
                isCompleted: !!defaultDate,
                isSubmitted: false,
            }

            state.cabanas = [...state.cabanas, newCabana]
        },

        updateCabana(
            state,
            action: PayloadAction<{
                index: number
                isSubmitted?: boolean
                date?: string
                quantity?: number
            }>
        ) {
            const { index, isSubmitted, date, quantity } = action.payload

            if (index < 0 || index > state.cabanas.length - 1) return

            if (isSubmitted !== undefined)
                state.cabanas[index].isSubmitted = isSubmitted

            if (date !== undefined) {
                state.cabanas[index].cabana.detail.date = date
                state.cabanas[index].cabana.quantity = DEFAULT_QUANTITY
            }
            if (quantity !== undefined)
                state.cabanas[index].cabana.quantity = quantity

            state.cabanas[index].isCompleted = checkIfCabanaCompleted(
                state.cabanas[index].cabana.detail.date
            )
        },

        setEditCabana(
            state,
            action: PayloadAction<{
                index: number
                editState: 'editing' | 'declined' | 'completed'
            }>
        ) {
            const { index, editState } = action.payload
            const { cabanas, edited } = state

            if (cabanas.length === 0 || index > cabanas.length - 1 || index < 0)
                return

            // restore state for currently edited cabana
            if (edited !== null) {
                if (editState !== 'completed')
                    state.cabanas[edited.index] = { ...edited.origin }

                state.edited = null
            }

            // set desired route as edited
            if (editState === 'editing') {
                state.edited = {
                    index,
                    origin: {
                        ...cabanas[index],
                        cabana: { ...cabanas[index].cabana },
                    },
                }
                state.cabanas[index].cabana.cartItemId = uuidv4()
                state.cabanas[index].cabana.detail.boxId = uuidv4()
            }
        },

        cleanCabanas(state) {
            const { edited, cabanas } = state

            if (edited !== null)
                state.cabanas[edited.index] = { ...edited.origin }

            state.cabanas = cabanas.filter((cabana) => cabana.isSubmitted)
            state.edited = null
        },

        removeCabana(
            state,
            action: PayloadAction<{
                index: number
            }>
        ) {
            const { index } = action.payload

            if (
                state.cabanas.length === 0 ||
                index > state.cabanas.length - 1 ||
                index < 0
            )
                return

            state.cabanas.splice(index, 1)
        },

        removeCabanas(state) {
            state.cabanas = []
            state.edited = null
        },

        synchronizeCabanasWithCart(
            state,
            action: PayloadAction<CabanaItemType[]>
        ) {
            state.cabanas = action.payload
        },
    },
})

export const {
    setInitialData,
    addCabana,
    updateCabana,
    setEditCabana,
    cleanCabanas,
    removeCabana,
    removeCabanas,
    synchronizeCabanasWithCart,
} = cabanaSlice.actions

export default cabanaSlice.reducer
